import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';

const log = container.get('Logger').getSubLogger({ name: 'FeatureFlag' });

export interface FeatureFlagOptions<T> {
    /**
     * The key created in the LaunchDarkly interface to identify the feature flag.
     */
    key: string;
    /**
     * The default value to use if the feature flag is not found or the user is not in the targeting.
     */
    defaultValue: T;
    /**
     * Optional function to parse the raw feature flag value into the expected type.
     */
    parseValue?: (value: unknown) => T;
}

export class FeatureFlag<T> {
    constructor(options: FeatureFlagOptions<T>) {
        this.#key = options.key;
        this.#defaultValue = options.defaultValue;
        this.#parseValue = options.parseValue;
    }

    #defaultValue: T;

    get defaultValue(): T {
        return this.#defaultValue;
    }

    #key: string;

    get key(): string {
        return this.#key;
    }

    #parseValue?: (value: unknown) => T;

    parseValue(value: unknown): T {
        if (this.#parseValue) {
            try {
                return this.#parseValue(value);
            } catch (err) {
                log.warn({
                    message: `Failed to parse feature flag value for key "${this.#key}". Using default value.`,
                    value,
                    err,
                });
                return this.#defaultValue;
            }
        }
        return value as T;
    }

    /**
     * @deprecated Use FeatureFlagService.getSignal() or FeatureFlagService.getValue() instead
     */
    select = (state: RootState): T => {
        return (state.featureFlags.flags[this.#key] as T) ?? this.#defaultValue;
    };
}
